import React, { useState } from "react";
import PersonalInfoForm from "./PersonalInfoForm";
import StoreDetailsForm from "./StoreDetailsForm";
import AudienceDataForm from "./AudienceDataForm";
import { toast, Toaster } from "react-hot-toast";
import { AdminServices } from "../../services/admin/AdminServices";
import { parse } from "date-fns";
import { AxiosError } from "axios";
import { CircularProgress } from "@mui/material";
type RegisterUserResponse = {
  response: {
    userId: string;
    password: string;
    status: "SUCCESS" | "FAILED"; // adjust as needed
  };
  errorMessage: string | null;
  errorCode: string | null;
};

type UserFormData = {
  name: string;
  email: string;
  organization: string;
  phone: string;
  type: "" | "ADVERTISER" | "MERCHANT" | "SIGNAGE";
  role: "" | "ADMIN";
  userType: "" | "BASIC" | "PREMIUM";
};

type StoreFormData = {
  storeName: string;
  addressLine1: string;
  addressLine2: string;
  locality: string;
  pincode: number | null;
  city: string;
  state: string;
  country: string;
  latitude: string; // store as number
  longitude: string;
  image?: string; // optional
  video?: string; // optional
};

type AudienceFormData = {
  openingHours: number;
  closingHours: number;
  category: string;
  subCategory: string;
  socialIndicator: string;
  persona: string[];
  incomeCategory: string[];
  gender: string;
  averageMonthlyFootfall: number;
  ageGroup: string[];
  notAllowedCategory: string[];
  notAllowedSubCategory: string[];
};

const AdminForm: React.FC = () => {
  const [counter, setCounter] = useState(1);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [registeredUserId, setRegisteredUserId] = useState<string>("");
  const [generatedPassword, setGeneratedPassword] = useState<string>("");

  const [userFormData, setUserFormData] = useState<UserFormData>({
    name: "",
    email: "",
    organization: "",
    phone: "",
    type: "",
    role: "",
    userType: "",
  });

  const [storeFormData, setStoreFormData] = useState<StoreFormData>({
    storeName: "",
    addressLine1: "",
    addressLine2: "",
    locality: "",
    pincode: null,
    city: "",
    state: "",
    country: "",
    latitude: "",
    longitude: "",
  });

  const [audienceFormData, setAudienceFormData] = useState<AudienceFormData>({
    openingHours: 0,
    closingHours: 0,
    category: "",
    subCategory: "",
    socialIndicator: "",
    persona: [],
    incomeCategory: [],
    gender: "",
    averageMonthlyFootfall: 0,
    ageGroup: [],
    notAllowedCategory: [],
    notAllowedSubCategory: [],
  });

  const handleRegister = async (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();

    const {
      openingHours,
      closingHours,
      category,
      subCategory,
      socialIndicator,
      persona,
      incomeCategory,
      gender,
      averageMonthlyFootfall,
      ageGroup,
      notAllowedCategory,
      notAllowedSubCategory,
    } = audienceFormData;

    const isAudienceFormIncomplete =
      !openingHours ||
      !closingHours ||
      !category ||
      !subCategory ||
      !socialIndicator ||
      !gender ||
      !averageMonthlyFootfall ||
      persona.length === 0 ||
      incomeCategory.length === 0 ||
      ageGroup.length === 0;

    if (isAudienceFormIncomplete) {
      toast.error("Please fill all the required fields in audience data.");
      return;
    }

    setLoading(true);

    try {
      const parseData: any = {
        email: userFormData.email,
        firstName: userFormData.name.split(" ")[0],
        lastName: userFormData.name.split(" ").slice(1).join(" ") || "",
        type: userFormData.type,
        role: userFormData.role,
        storeName: storeFormData.storeName,
        category: audienceFormData.category,
        subCategory: audienceFormData.subCategory,
        socialIndicator: audienceFormData.socialIndicator,
        userCategory: userFormData.userType,
        phoneNumber: userFormData.phone,
        openingHourInMinutes: Number(audienceFormData.openingHours),
        closingHourInMinutes: Number(audienceFormData.closingHours),
        persona: audienceFormData.persona,
        incomeCategories: audienceFormData.incomeCategory,
        gender: audienceFormData.gender,
        avgMonthlyFootfall: Number(audienceFormData.averageMonthlyFootfall),
        ageGroups: audienceFormData.ageGroup,
        organization: {
          name: userFormData.organization,
        },
        address: {
          line1: storeFormData.addressLine1,
          line2: storeFormData.addressLine2 || "",
          state: storeFormData.state,
          country: storeFormData.country,
          city: storeFormData.city,
          zip: String(storeFormData.pincode),
          locality: storeFormData.locality,
          lat: String(storeFormData.latitude),
          lng: String(storeFormData.longitude),
        },
      };

      if (storeFormData.image) {
        parseData.image = storeFormData.image;
      }

      if (storeFormData.video) {
        parseData.video = storeFormData.video;
      }

      // Conditionally add optional fields
      if (notAllowedCategory?.length > 0) {
        parseData.notAllowedCategories = notAllowedCategory.map((item) =>
          String(item)
        );
      }

      if (notAllowedSubCategory?.length > 0) {
        parseData.notAllowedSubcategories = notAllowedSubCategory.map((item) =>
          String(item)
        );
      }
      const result: RegisterUserResponse = await AdminServices.registerUser(
        parseData
      );

      if (result.response?.status === "SUCCESS") {
        setRegisteredUserId(result.response.userId);
        setGeneratedPassword(result.response.password);
        toast.success("User registered successfully!");
        setModalOpen(true);

        // Reset form state
        setUserFormData({
          name: "",
          email: "",
          organization: "",
          phone: "",
          type: "",
          role: "",
          userType: "",
        });

        setStoreFormData({
          storeName: "",
          addressLine1: "",
          addressLine2: "",
          locality: "",
          pincode: null,
          city: "",
          state: "",
          country: "",
          latitude: "",
          longitude: "",
        });

        setAudienceFormData({
          openingHours: 0,
          closingHours: 0,
          category: "",
          subCategory: "",
          socialIndicator: "",
          persona: [],
          incomeCategory: [],
          gender: "",
          averageMonthlyFootfall: 0,
          ageGroup: [],
          notAllowedCategory: [],
          notAllowedSubCategory: [],
        });

        setCounter(1);
        setLoading(false);
      } else {
        setLoading(false);
        toast.error(result.errorMessage || "Registration failed.");
      }
    } catch (error) {
      console.error("Error registering user:", error);
      const axiosError = error as AxiosError;
      setCounter(1);
      if (axiosError.response && axiosError.response.status === 409) {
        toast.error("Email ID already exists. Please use a different email.");
      } else {
        toast.error("An unexpected error occurred during registration.");
      }
      setLoading(false);
    } finally {
      setLoading(false);
    }
  };

  const handleCloseModal = () => {
    setModalOpen(false);
  };

  const handleCopy = (text: string) => {
    navigator.clipboard.writeText(text);
    toast.success("Copied to clipboard!");
  };

  return (
    <>
      <Toaster position="top-right" reverseOrder={false} />
      {loading ? (
        <div className="flex items-center justify-center w-full h-[50vh]">
          <CircularProgress />
        </div>
      ) : (
        <div
          className={`multistep-form-container  z-10  bg-white overflow-y-auto rounded-xl min-h-[80vh] flex flex-col pb-10`}
        >
          <div
            className={`top-toggle-tabs ${
              modalOpen && "blur-md opacity-50"
            } h-[20vh] text-[0.9rem] flex items-center justify-around`}
          >
            <div className="flex items-center personal-info">
              <span
                className={`${
                  counter === 1
                    ? "bg-blue-600 text-white scale-105 p-4 text-[1.1rem] "
                    : ""
                } h-[4vh] w-[4vh] flex justify-center items-center mr-2 border border-neutral-300 rounded-full`}
              >
                1
              </span>
              <span
                className={`${
                  counter === 1 && "font-bold text-neutral-700 "
                } text-neutral-500`}
              >
                Personal Info
              </span>
            </div>

            <div className="flex items-center store-details">
              <span
                className={`${
                  counter === 2
                    ? "bg-blue-600 text-white scale-105 p-4 text-[1.1rem] "
                    : ""
                } h-[4vh] w-[4vh] flex justify-center items-center mr-2 border border-neutral-300 rounded-full`}
              >
                2
              </span>
              <span
                className={`${
                  counter === 2 && "font-bold text-neutral-700 "
                } text-neutral-500`}
              >
                Store Details
              </span>
            </div>

            <div className="flex items-center audience-info">
              <span
                className={`${
                  counter === 3
                    ? "bg-blue-600 text-white scale-105 p-4 text-[1.1rem] "
                    : ""
                } h-[4vh] w-[4vh] flex justify-center items-center mr-2 border border-neutral-300 rounded-full`}
              >
                3
              </span>
              <span
                className={`${
                  counter === 3 && "font-bold text-neutral-700 "
                } text-neutral-500`}
              >
                Store Audience Data
              </span>
            </div>
          </div>

          <div
            className={`form-container ${
              modalOpen && "blur-md opacity-50"
            } h-[80%] px-10`}
          >
            {counter === 1 && (
              <PersonalInfoForm
                userFormData={userFormData}
                setUserFormData={setUserFormData}
                counter={counter}
                setCounter={setCounter}
              />
            )}

            {counter === 2 && (
              <StoreDetailsForm
                storeFormData={storeFormData}
                setStoreFormData={setStoreFormData}
                counter={counter}
                setCounter={setCounter}
              />
            )}

            {counter === 3 && (
              <>
                <AudienceDataForm
                  audienceFormData={audienceFormData}
                  setAudienceFormData={setAudienceFormData}
                  counter={counter}
                  setCounter={setCounter}
                />

                <div className="flex justify-center mt-10">
                  <button
                    type="submit"
                    className="px-4 py-2 font-semibold text-white transition rounded bg-violet-600 hover:bg-violet-700"
                    onClick={(e) => handleRegister(e)}
                    disabled={loading}
                  >
                    {loading ? "Registering..." : "Register"}
                  </button>
                </div>
              </>
            )}
          </div>

          {/* Success Modal - You can add this similar to the ScreenForm if needed */}
          {modalOpen && (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
              <div className="bg-white rounded-lg p-6 w-[30vw]">
                <h3 className="mb-4 text-xl font-bold text-violet-600">
                  Registration Successful
                </h3>
                <p className="mb-2 text-gray-700">
                  User has been registered successfully!
                </p>

                <div className="p-4 mt-4 space-y-4 bg-gray-100 rounded-md">
                  <div className="flex items-center justify-between">
                    <p>
                      <strong>User ID:</strong> {registeredUserId}
                    </p>
                    <button
                      onClick={() => handleCopy(registeredUserId)}
                      className="text-sm text-blue-500 hover:text-blue-700"
                    >
                      📋
                    </button>
                  </div>

                  <div className="flex items-center justify-between">
                    <p>
                      <strong>Password:</strong> {generatedPassword}
                    </p>
                    <button
                      onClick={() => handleCopy(generatedPassword)}
                      className="text-sm text-blue-500 hover:text-blue-700"
                    >
                      📋
                    </button>
                  </div>
                </div>

                <div className="flex justify-end mt-6">
                  <button
                    onClick={handleCloseModal}
                    className="px-4 py-2 font-semibold text-white rounded bg-violet-600 hover:bg-violet-700"
                  >
                    OK
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </>
  );
};

export default AdminForm;